import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import CommonHeading from "../components/common/CommonHeading";
import { signedFetch } from "../components/context/AuthContext";

export default function Contact() {
  // Set up state to hold form data and error messages
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    number: "",
    message: "",
  });

  const [isLoading, setIsLoading] = useState(false); // Loading state
  const [messageSent, setMessageSent] = useState(false); // Show success popup
  const [submitError, setSubmitError] = useState("");
  const [errors, setErrors] = useState({
    name: false,
    email: false,
    number: false,
    message: false,
  });

  const [settings, setSettings] = useState(() => {
    try {
      const s = localStorage.getItem("hk_resort_settings");
      if (s) return JSON.parse(s);
    } catch (e) {}
    return null;
  });

  React.useEffect(() => {
    const handleStorage = () => {
      try {
        const s = localStorage.getItem("hk_resort_settings");
        if (s) setSettings(JSON.parse(s));
      } catch (e) {}
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  const bookingEmail = settings?.bookingEmail || "reservation@harikunjresort.com";
  const techEmail = settings?.techEmail || "reservation@harikunjresort.com";
  const generalEmail = settings?.generalEmail || "reservation@harikunjresort.com";

  const renderMultiLinks = (str, type = "email") => {
    if (!str) return null;
    const tokens = str.split(/([&/,])/);
    return tokens.map((token, idx) => {
      const trimmed = token.trim();
      if (trimmed === "&" || trimmed === "/" || trimmed === ",") {
        return <span key={`sep-${idx}`} className="mx-1 text-muted fw-bold">{trimmed}</span>;
      }
      if (!trimmed) return null;
      if (type === "email") {
        return (
          <a key={`em-${idx}`} href={`mailto:${trimmed}`} className="text-primary text-decoration-none fw-bold me-1">
            {trimmed}
          </a>
        );
      }
      const numOnly = trimmed.replace(/[^0-9+]/g, "");
      return (
        <a key={`ph-${idx}`} href={`tel:${numOnly}`} className="text-primary text-decoration-none fw-bold me-1">
          {trimmed}
        </a>
      );
    });
  };

  const dynamicContactCards = [
    {
      icon: <i className="fa fa-envelope-open text-primary me-2"></i>,
      title: "Booking",
      email: bookingEmail,
    },
    {
      icon: <i className="fa fa-envelope-open text-primary me-2"></i>,
      title: "Technical",
      email: techEmail,
    },
    {
      icon: <i className="fa fa-envelope-open text-primary me-2"></i>,
      title: "General",
      email: generalEmail,
    },
  ];

  // Handle input change and update the state
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));

    // Clear the error highlight when user starts typing
    if (name === "email" || name === "number" || name === "name" || name === "message") {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: false,
      }));
    }
  };

  // Validate form data and highlight empty fields
  const validateForm = () => {
    const { name, email, number, message } = formData;
    const newErrors = {
      name: !name,
      email: !email,
      number: !number,
      message: !message,
    };

    setErrors(newErrors);

    return !Object.values(newErrors).includes(true); // If any value is true, the form is invalid
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Form validation
    if (!validateForm()) return;
  
    setIsLoading(true); // Set loading state to true
  
    // Save to local cache first
    try {
      const existingInquiries = JSON.parse(localStorage.getItem("contact_inquiries") || "[]");
      const newInquiry = {
        id: Date.now().toString(),
        name: formData.name,
        email: formData.email,
        number: formData.number,
        message: formData.message,
        timestamp: new Date().toISOString(),
        type: "Contact Inquiry",
        status: "Pending"
      };
      existingInquiries.push(newInquiry);
      localStorage.setItem("contact_inquiries", JSON.stringify(existingInquiries));
    } catch (err) {
      console.warn("localStorage write failed", err);
    }

    signedFetch({
      action: "createSupportQuery",
      name: formData.name,
      fullName: formData.name,
      email: formData.email,
      number: formData.number,
      message: formData.message,
    })
      .then((data) => {
        setIsLoading(false);
        if (data && data.success) {
          setMessageSent(true);
          setFormData({
            name: "",
            email: "",
            number: "",
            message: "",
          });
        } else {
          setSubmitError(data?.error || "Failed to submit inquiry. Please check your inputs and try again.");
        }
      })
      .catch((error) => {
        setIsLoading(false);
        console.error("API connection error:", error);
        setSubmitError("Network connection error. Please try again.");
      });
  };

  const handleClosePopup = () => {
    setMessageSent(false); // Close the popup manually
  };

  return (
    <>
      <Helmet>
        <title>Contact Us | HariKunj Resort Jodhpur</title>
        <meta
          name="description"
          content="Contact HariKunj Resort in Jodhpur for reservations, wedding event inquiries, stay tariffs, and Google Maps location details."
        />
        <link rel="canonical" href="https://harikunjresort.com/contact" />
        <meta property="og:title" content="Contact Us | HariKunj Resort Jodhpur" />
        <meta property="og:description" content="Contact HariKunj Resort in Jodhpur for reservations, wedding event inquiries, stay tariffs, and Google Maps location details." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://harikunjresort.com/contact" />
      </Helmet>

      <h1 className="visually-hidden">
        Contact HariKunj Resort Jodhpur — Direct Phone, Email & Location Guide
      </h1>

      <div className="container-xxl hk-top-section pb-5" style={{ background: "linear-gradient(180deg, #FAF6F0 0%, #F2EAE0 50%, #FAF6F0 100%)", color: "#1A283B" }}>
        <div className="container">
          <CommonHeading
            heading="Contact Us"
            subtitle="Contact "
            title="For Any Query"
          />
          <div className="row g-4">
            <div className="col-12">
              <div className="row gy-4">
                {dynamicContactCards.map((item, index) => (
                  <div className="col-md-4" key={index}>
                    <h6 className="section-title text-start text-primary text-uppercase">
                      {item.title}
                    </h6>
                    <p className="d-flex align-items-center flex-wrap">
                      {item.icon}
                      {renderMultiLinks(item.email, "email")}
                    </p>
                  </div>
                ))}
              </div>
            </div>
            <div className="col-md-6 wow fadeIn" data-wow-delay="0.1s">
              <div className="hk-map-card h-100">
                <iframe
                  title="HariKunj Resort Location Map"
                  className="w-100 h-100"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1130856.702227883!2d72.948499!3d26.255936!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x39418f000b55d101%3A0xa2ed50eba37393b8!2sHari%20Kunj%20Resort%2C%20Gopalwadi!5e0!3m2!1sen!2sin!4v1671029946650!5m2!1sen!2sin"
                  frameBorder="0"
                  style={{ border: "0", minHeight: "450px", display: "block" }}
                  allowFullScreen=""
                  aria-hidden="false"
                  tabIndex="0"
                ></iframe>
              </div>
            </div>
            <div className="col-md-6">
              <div className="wow fadeInUp h-100" data-wow-delay="0.2s">
                <div className="hk-contact-card h-100">
                  {submitError && <div className="alert alert-danger mb-3" role="alert">{submitError}</div>}

                  <form onSubmit={handleSubmit}>
                    <div className="row g-3">
                      <div className="col-md-6">
                        <div className="unique-form-group">
                          <label htmlFor="name">Your Name</label>
                          <div className="unique-input-icon-wrapper">
                            <input
                              type="text"
                              className={`unique-dojo-input ${errors.name ? "is-invalid" : ""}`}
                              id="name"
                              placeholder="Your Name"
                              name="name"
                              value={formData.name}
                              onChange={handleInputChange}
                            />
                            <i className="fa fa-user unique-field-icon"></i>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="unique-form-group">
                          <label htmlFor="email">Your Email</label>
                          <div className="unique-input-icon-wrapper">
                            <input
                              type="email"
                              className={`unique-dojo-input ${errors.email ? "is-invalid" : ""}`}
                              id="email"
                              placeholder="Your Email"
                              name="email"
                              value={formData.email}
                              onChange={handleInputChange}
                            />
                            <i className="fa fa-envelope unique-field-icon"></i>
                          </div>
                        </div>
                      </div>
                      <div className="col-12">
                        <div className="unique-form-group">
                          <label htmlFor="number">Phone Number</label>
                          <div className="unique-input-icon-wrapper">
                            <input
                              type="tel"
                              className={`unique-dojo-input ${errors.number ? "is-invalid" : ""}`}
                              id="number"
                              placeholder="Phone Number (with country code)"
                              name="number"
                              value={formData.number}
                              onChange={handleInputChange}
                              maxLength="20"
                            />
                            <i className="fa fa-phone unique-field-icon"></i>
                          </div>
                        </div>
                      </div>
                      <div className="col-12">
                        <div className="unique-form-group">
                          <label htmlFor="message">Message</label>
                          <div className="unique-input-icon-wrapper">
                            <textarea
                              className={`unique-dojo-input ${errors.message ? "is-invalid" : ""}`}
                              placeholder="Leave a message here"
                              id="message"
                              name="message"
                              style={{ height: "120px", paddingLeft: "48px", paddingTop: "13px" }}
                              value={formData.message}
                              onChange={handleInputChange}
                            ></textarea>
                            <i className="fa fa-comment unique-field-icon" style={{ top: "25px", transform: "none" }}></i>
                          </div>
                        </div>
                      </div>
                      <div className="col-12 mt-4">
                        <button
                          className="hk-submit-btn py-3 w-100"
                          type="submit"
                          disabled={isLoading}
                        >
                          {isLoading ? "Sending..." : "Send Message"}
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Premium Success Popup with Backdrop Blur ── */}
      {messageSent && (
        <>
          {/* Blurred backdrop overlay */}
          <div
            className="hk-popup-backdrop"
            onClick={handleClosePopup}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 998,
              background: "rgba(5, 14, 26, 0.6)",
              backdropFilter: "blur(12px)",
              WebkitBackdropFilter: "blur(12px)",
              animation: "hkFadeIn 0.3s ease-out",
            }}
          />

          {/* Popup card */}
          <div
            className="hk-success-popup"
            style={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 999,
              width: "min(420px, 90vw)",
              background: "linear-gradient(145deg, rgba(11, 29, 55, 0.95), rgba(5, 14, 26, 0.98))",
              border: "1px solid rgba(212, 175, 55, 0.3)",
              borderRadius: "20px",
              padding: "40px 32px 32px",
              textAlign: "center",
              boxShadow: "0 25px 60px rgba(0,0,0,0.5), 0 0 40px rgba(212,175,55,0.08), inset 0 1px 0 rgba(255,255,255,0.05)",
              animation: "hkPopIn 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
            }}
          >
            {/* Close × button */}
            <button
              onClick={handleClosePopup}
              style={{
                position: "absolute",
                top: "14px",
                right: "16px",
                background: "rgba(255,255,255,0.06)",
                border: "1px solid rgba(255,255,255,0.1)",
                color: "rgba(255,255,255,0.5)",
                fontSize: "18px",
                width: "32px",
                height: "32px",
                borderRadius: "50%",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "all 0.25s ease",
                lineHeight: 1,
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = "rgba(212,175,55,0.15)";
                e.currentTarget.style.color = "#d4af37";
                e.currentTarget.style.borderColor = "rgba(212,175,55,0.4)";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = "rgba(255,255,255,0.06)";
                e.currentTarget.style.color = "rgba(255,255,255,0.5)";
                e.currentTarget.style.borderColor = "rgba(255,255,255,0.1)";
              }}
            >
              ×
            </button>

            {/* Golden checkmark circle */}
            <div style={{
              width: "72px",
              height: "72px",
              margin: "0 auto 20px",
              borderRadius: "50%",
              background: "linear-gradient(135deg, rgba(212,175,55,0.15), rgba(212,175,55,0.05))",
              border: "2px solid rgba(212,175,55,0.4)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              animation: "hkCheckPulse 2s ease-in-out infinite",
            }}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#d4af37" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>

            {/* Heading */}
            <h4 style={{
              color: "#fff",
              fontFamily: "'Playfair Display', serif",
              fontSize: "1.5rem",
              fontWeight: 700,
              margin: "0 0 8px",
              letterSpacing: "0.02em",
            }}>
              Thank You!
            </h4>

            {/* Subtext */}
            <p style={{
              color: "rgba(255,255,255,0.6)",
              fontSize: "0.95rem",
              margin: "0 0 6px",
              lineHeight: 1.5,
            }}>
              Your enquiry has been received successfully.
            </p>
            <p style={{
              color: "rgba(212,175,55,0.8)",
              fontSize: "0.85rem",
              margin: "0 0 28px",
              fontWeight: 500,
            }}>
              We'll connect with you within 24 hours!
            </p>

            {/* Decorative divider */}
            <div style={{
              width: "60px",
              height: "2px",
              margin: "0 auto 24px",
              background: "linear-gradient(90deg, transparent, rgba(212,175,55,0.5), transparent)",
              borderRadius: "2px",
            }} />

            {/* Close button */}
            <button
              onClick={handleClosePopup}
              style={{
                padding: "12px 40px",
                background: "linear-gradient(135deg, #d4af37, #b8962e)",
                color: "#0b1d37",
                border: "none",
                borderRadius: "12px",
                cursor: "pointer",
                fontSize: "0.95rem",
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                transition: "all 0.3s ease",
                boxShadow: "0 4px 20px rgba(212,175,55,0.3)",
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = "0 8px 30px rgba(212,175,55,0.45)";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 4px 20px rgba(212,175,55,0.3)";
              }}
            >
              Got It
            </button>
          </div>
        </>
      )}
      <style>{`
        .hk-contact-card {
          background: linear-gradient(135deg, rgba(11, 29, 55, 0.96), rgba(5, 14, 26, 0.98));
          padding: 40px;
          border-radius: 24px;
          box-shadow: 0 20px 50px rgba(5, 14, 26, 0.45);
          border: 1.5px solid rgba(212, 175, 55, 0.55);
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
        }

        .hk-map-card {
          border-radius: 24px;
          overflow: hidden;
          border: 1.5px solid rgba(212, 175, 55, 0.55);
          box-shadow: 0 20px 50px rgba(5, 14, 26, 0.45);
          transition: all 0.3s ease;
          background: #050e1a;
        }

        .hk-map-card:hover {
          border-color: #FEA116;
          box-shadow: 0 24px 60px rgba(254, 161, 22, 0.25);
        }

        .unique-form-group {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .unique-form-group label {
          font-weight: 600;
          color: rgba(250, 246, 240, 0.8);
          font-size: 0.75rem;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          font-family: 'Poppins', sans-serif;
          margin-bottom: 2px;
        }

        .hk-submit-btn {
          width: 100%;
          border-radius: 14px;
          border: 1.5px solid rgba(255, 255, 255, 0.3);
          background: linear-gradient(135deg, #FEA116, #D4AF37);
          color: #050E1A;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .hk-submit-btn:hover:not(:disabled) {
          background: linear-gradient(135deg, #ff8c00, #fea116);
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(254, 161, 22, 0.45);
        }
        
        .hk-submit-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        /* ── Popup Animations ── */
        @keyframes hkFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes hkPopIn {
          0% { opacity: 0; transform: translate(-50%, -50%) scale(0.7); }
          100% { opacity: 1; transform: translate(-50%, -50%) scale(1); }
        }

        @keyframes hkCheckPulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(212,175,55,0.2); }
          50% { box-shadow: 0 0 0 12px rgba(212,175,55,0); }
        }
      `}</style>
    </>
  );
}
